import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BortalLogo from "./BortalLogo";
import { Button } from "./ui/button";
import { useAuth } from "@/hooks/useAuth";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { User, LogOut } from "lucide-react";
import { ik } from "@/lib/imageOptimization";
const Navbar = () => {
  const {
    user,
    isAdmin,
    signOut
  } = useAuth();
  const {
    data: fanProfile
  } = useQuery({
    queryKey: ["navbarFanProfile", user?.id],
    queryFn: async () => {
      const {
        data
      } = await supabase.from("fan_profiles").select("profile_image, display_name, username").eq("user_id", user!.id).maybeSingle();
      return data;
    },
    enabled: !!user?.id
  });
  return <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      {/* Desktop nav links - centered to full page width */}
      <div className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none">
        <div className="flex items-center gap-10 pointer-events-auto">
          <Link to="/artists" className="text-muted-foreground hover:text-foreground transition-colors text-lg font-semibold">
            Artists
          </Link>
          {FEATURE_FLAGS.VENUES_ENABLED && <Link to="/venues" className="text-muted-foreground hover:text-foreground transition-colors text-lg font-semibold">
              Venues
            </Link>}
          {FEATURE_FLAGS.GIGS_ENABLED && <Link to="/gigs" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Gigs
            </Link>}
          <Link to="/news" className="text-muted-foreground hover:text-foreground transition-colors text-lg font-semibold">
            News
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between gap-6">
          <Link to="/" className="text-foreground hover:text-foreground/80 transition-colors" aria-label="Bortal home">
            <BortalLogo />
          </Link>

          {/* Mobile nav links */}
          <div className="flex md:hidden items-center justify-center gap-6 flex-1">
            <Link to="/artists" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Artists
            </Link>
            {FEATURE_FLAGS.VENUES_ENABLED && <Link to="/venues" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Venues
              </Link>}
            <Link to="/news" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              News
            </Link>
          </div>

          {/* Right side: Admin link + User menu */}
          <div className="hidden md:flex items-center gap-4">
            {isAdmin &&
          <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Admin
              </Link>
          }
            {user ?
          <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background" aria-label="User menu">
                    {fanProfile &&
                <span className="hidden sm:block text-sm font-medium text-foreground max-w-[120px] truncate">
                        {fanProfile.display_name || `@${fanProfile.username}`}
                      </span>
                }
                    <Avatar className="h-8 w-8 cursor-pointer">
                      <AvatarImage src={fanProfile?.profile_image ? ik(fanProfile.profile_image, 32, 32) || undefined : undefined} alt={fanProfile?.display_name || "Profile"} className="object-cover" />
                      <AvatarFallback className="bg-muted text-muted-foreground">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/me" className="flex items-center cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> :

          <Link to="/auth">
                <Button size="sm">Log in</Button>
              </Link>
          }
          </div>

          {/* Mobile: User menu only */}
          <div className="flex md:hidden items-center">
            {user ?
          <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background" aria-label="User menu">
                    <Avatar className="h-8 w-8 cursor-pointer">
                      <AvatarImage src={fanProfile?.profile_image ? ik(fanProfile.profile_image, 32, 32) || undefined : undefined} alt={fanProfile?.display_name || "Profile"} className="object-cover" />
                      <AvatarFallback className="bg-muted text-muted-foreground">
                        <User className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {isAdmin &&
              <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center cursor-pointer">
                          Admin
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
              }
                  <DropdownMenuItem asChild>
                    <Link to="/me" className="flex items-center cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu> :

          <Link to="/auth">
                <Button size="sm">Log in</Button>
              </Link>
          }
          </div>
        </div>
      </div>
    </nav>;
};
export default Navbar;